import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'
import cn from 'classnames'
import { TwitterShareButton, FacebookShareButton } from 'react-share'
import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { getVolunteerEventMapEmbedProps } from '../../helpers/volunteerEventLocation'
import { ROUTE_NAMES } from '../../constants/route-names'
import { Loader } from '../../kit/components/loader'
import { TimeAndPlace } from '../../kit/components/time-and-place-block'
import IconComponent from '../../kit/components/icon'
import { WhoIsNeeded } from '../../kit/components/who-is-needed'
import CancelRegistrationPopup from '../../kit/modules/events/cancel-registration-popup'
import ENDPOINT_ROUTES from '../../constants/APIs'
import FeatureEnabled from '../../kit/modules/features/FeatureEnabled'
import {
  mapApiOpportunityToEventDetails,
  useEventListDispatch,
  useEventListState,
} from '../../contexts/EventListContext'
import { FeatureEnabledState, getFeatureEnabled, withFeatureEnabled } from '../../contexts/FeatureContext'
import { isEffectiveAllowMultipleRegistrations } from '../../helpers/allowMultipleRegistrations'
import { encodeOccurrence, decodeOccurrenceId } from '../../helpers/encodeOccurrence'
import { EmbeddedGoogleMap } from './EmbeddedGoogleMap'
import { EventDetailsBack } from '../../navigation/EventDetailsBack'

import './styles.scss'

interface NotFoundProps {
  fallback: { url: string; label: string }
  children: any
}

interface Props {
  match?: any
  history?: any
  location?: any
  isMyEvent?: boolean
  currentUser?: any
  getJSON?: (endpoint: string) => Promise<any>
  setOpenLogin?: (isOpen: boolean, shouldRedirectToRegister?: boolean) => void
  data?: any
  featureEnabledState?: FeatureEnabledState
}

export enum OpportunityStatus {
  Draft = 'Draft',
  Concluded = 'Concluded',
  Live = 'Live',
  Upcoming = 'Upcoming',
}

interface EventDetails {
  name: string
  organization: {
    website: string
    name: string
    id: number | null
  }
  description: string
  instructions: string
  timeshifts: any[]
  address: string
  missingRequiredMemberships?: boolean
  city: string
  state: string
  zip: string
  mapPosition: {
    lat: number
    lng: number
  }
  locationDetails: string
  locationIsAddress: boolean
  locationAddressType?: string | null
  startsAt: string
  endsAt: string
  timeZone: any
  details: any[]
  id: number | null
  sourceType: 'opportunity' | 'registration' | ''
  opportunityId: number | string
  status?: OpportunityStatus
  allowMultipleRegistrations?: boolean
}

const initialDetails: EventDetails = {
  name: '',
  organization: {
    website: '',
    name: '',
    id: null,
  },
  description: '',
  instructions: '',
  timeshifts: [],
  address: '',
  city: '',
  state: '',
  zip: '',
  mapPosition: {
    lng: 0,
    lat: 0,
  },
  locationDetails: '',
  locationIsAddress: true,
  locationAddressType: null,
  startsAt: '',
  endsAt: '',
  timeZone: null,
  details: [],
  id: null,
  sourceType: '',
  opportunityId: '',
}

const VolunteersEventDetailsScene = (props: Props) => {
  const [apiError, setApiError] = useState('')
  const [apiErrorMessage, setApiErrorMessage] = useState('')
  const [details, setDetails] = useState(initialDetails)
  const [allRoles, setAllRoles] = useState(null)
  const [cancelPopup, setCancelPopup] = useState(false)
  const [cancelRegistrationId, setCancelRegistrationId] = useState<number | null>(null)
  const [myRegistrations, setMyRegistrations] = useState<any[]>([])
  const [availableSlots, setAvailableSlots] = useState<number | null>(null)
  const [roleById, setRoleById] = useState([])
  const [eventId, setEventId] = useState(props.match?.params.eventId)
  const { currentUser, getJSON, history, isMyEvent, match, setOpenLogin, data, featureEnabledState } = props
  const { token } = queryString.parse(props.location?.search || '')
  const { organizationId } = props.match?.params || {}
  const pathName = window.location.href
  const isUserSignedIn = useRef(!isEmpty(currentUser) && !currentUser.error)
  const { events } = useEventListState()
  const eventListDispatch = useEventListDispatch()
  const { redirectToRegisterAfterLogin } = data || {}

  const backFallback = useMemo(
    () => ({
      url: isMyEvent ? ROUTE_NAMES.myEvents(organizationId) : ROUTE_NAMES.calendar(organizationId),
      label: isMyEvent ? 'My Events' : 'Events',
    }),
    [isMyEvent, organizationId],
  )

  const dispatchFromStableLink = async () => {
    if (!getJSON) return

    let response

    const endpoint = ENDPOINT_ROUTES.Events.getStableLinkedOccurrence(match?.params.stableLinkCode, {
      includeMissingRequiredMemberships: true,
    })

    response = await getJSON(endpoint)

    if (response.error) {
      setApiError(response.error)
      setApiErrorMessage(response.message)
      return
    }

    Object.assign(response, { stableLink: match?.params.stableLinkCode })

    eventListDispatch({ type: 'opportunities', payload: [response] })
  }

  const dispatchFromNewCall = async (type: 'registrations' | 'opportunities') => {
    if (!getJSON) return

    let response

    if (type === 'opportunities') {
      const endpoint = ENDPOINT_ROUTES.Events.getOccurrence(
        match?.params.eventId,
        token
          ? {
              token: String(token),
              includeMissingRequiredMemberships: true,
            }
          : {
              includeMissingRequiredMemberships: true,
            },
      )

      response = await getJSON(endpoint)
    } else {
      const endpoint = ENDPOINT_ROUTES.Events.getMyEventByIdOccurrenceRefactor(eventId)

      response = await getJSON(endpoint)
    }

    if (response.error) {
      setApiError(response.error)
      setApiErrorMessage(response.message)
      return
    }

    eventListDispatch({ type, payload: [response] })
  }

  const applyRegistrationLookup = (storedOpportunity: EventDetails, registrationResponse: any[]) => {
    if (!registrationResponse.length) {
      setMyRegistrations([])
      setDetailsAndRoles(storedOpportunity)
      return
    }

    const allowMultipleRegistrations = isEffectiveAllowMultipleRegistrations(
      featureEnabledState,
      storedOpportunity.allowMultipleRegistrations || registrationResponse[0]?.opportunity?.allowMultipleRegistrations,
    )

    if (allowMultipleRegistrations) {
      setMyRegistrations(registrationResponse)
      setDetailsAndRoles({
        ...storedOpportunity,
        allowMultipleRegistrations: true,
        sourceType: 'opportunity',
        details: [],
      })
      return
    }

    setMyRegistrations([])
    setEventId(registrationResponse[0].id)
    eventListDispatch({ type: 'registrations', payload: [registrationResponse[0]] })
  }

  const attemptRegistrationUpgradeFromOpportunity = async (storedOpportunity: EventDetails) => {
    if (!getJSON) return

    const endpoint = ENDPOINT_ROUTES.Events.getMyEventsOccurrenceRefactor({
      encodedInstance: encodeOccurrence(
        storedOpportunity.opportunityId,
        storedOpportunity.startsAt,
        storedOpportunity.timeZone,
      ),
    })
    const registrationResponse = await getJSON(endpoint)

    if (!registrationResponse?.length) {
      if (storedOpportunity) {
        setMyRegistrations([])
        setDetailsAndRoles(storedOpportunity)
        return
      }

      dispatchFromNewCall('opportunities')
      return
    }

    applyRegistrationLookup(storedOpportunity, registrationResponse)
  }

  const attemptRegistrationUpgradeFromOpportunityParams = async (encodedInstance: string) => {
    if (!getJSON) return

    const endpoint = ENDPOINT_ROUTES.Events.getMyEventsOccurrenceRefactor({ encodedInstance })

    const registrationResponse = await getJSON(endpoint)

    if (!registrationResponse?.length) {
      dispatchFromNewCall('opportunities')
      return
    }

    const opportunityDetails = mapApiOpportunityToEventDetails(
      {
        ...registrationResponse[0].opportunity,
        id: registrationResponse[0].opportunity.id,
      },
      'opportunity',
    )
    applyRegistrationLookup(opportunityDetails, registrationResponse)
  }

  const setDetailsAndRoles = async (nextDetailsOnRender: EventDetails) => {
    setDetails(nextDetailsOnRender)

    if (nextDetailsOnRender.organization.id && getJSON) {
      const endpoint = ENDPOINT_ROUTES.Events.getRolesOccurrenceRefactor(nextDetailsOnRender.organization.id)
      const responseRoles = await getJSON(endpoint)
      setAllRoles(responseRoles)
    }

    const timeShifts = nextDetailsOnRender.timeshifts && nextDetailsOnRender.timeshifts.flat()

    if (nextDetailsOnRender.sourceType === 'registration' && !nextDetailsOnRender.allowMultipleRegistrations) {
      setAvailableSlots(0)
    }

    if (
      (nextDetailsOnRender.sourceType === 'opportunity' || nextDetailsOnRender.allowMultipleRegistrations) &&
      timeShifts.length
    ) {
      const roles = timeShifts.map((item: any) => {
        return item.roles
      })

      const rolesArr: any = roles.flat()
      setRoleById(rolesArr)

      const availableSlot = rolesArr.map((item: any) => {
        return item.available
      })

      const totalAvailable = availableSlot.reduce((a: number, b: number) => a + b, 0)
      setAvailableSlots(totalAvailable)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })

    let contextMatch = null
    let hasStoredOpportunity = false
    let hasStoredRegistration = false

    if (events.length > 0) {
      const isListOfOpportunities = events[0].sourceType === 'opportunity'

      if (match?.params.stableLinkCode) {
        contextMatch = events.find(({ stableLink }) => stableLink === match?.params.stableLinkCode)
      } else if (!isMyEvent) {
        const decodedParams = decodeOccurrenceId(match?.params.eventId)

        const timeZone = events[0]?.timeZone || 'UTC'

        contextMatch = events.find(
          ({ startsAt, opportunityId }) =>
            moment.tz(startsAt, timeZone).format('YYYY-MM-DD') === decodedParams.date &&
            opportunityId === decodedParams.opportunityId,
        )
      } else {
        contextMatch = isListOfOpportunities
          ? null
          : events.find(({ id, sourceType }) => id === parseInt(eventId) && sourceType === 'registration')
      }
    }

    hasStoredRegistration = contextMatch && contextMatch.sourceType === 'registration'
    hasStoredOpportunity = contextMatch && contextMatch.sourceType === 'opportunity'

    if (hasStoredRegistration || (hasStoredOpportunity && !isUserSignedIn.current)) {
      setDetailsAndRoles(contextMatch)
      return
    }

    if (hasStoredOpportunity && isUserSignedIn.current) {
      attemptRegistrationUpgradeFromOpportunity(contextMatch)
      return
    }

    if (isMyEvent) {
      dispatchFromNewCall('registrations')
      return
    }

    if (match?.params.stableLinkCode) {
      dispatchFromStableLink()
      return
      //TODO possible bug from not returning `setOpenLogin(false)`?  See below
    }

    if (!isMyEvent && isUserSignedIn.current) {
      // in this case, the eventId should be an encoded instance in the URL
      attemptRegistrationUpgradeFromOpportunityParams(eventId)
      return
    }

    dispatchFromNewCall('opportunities')

    return function () {
      if (setOpenLogin) {
        setOpenLogin(false)
      }
    }
  }, [events]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isUserSignedIn.current && !isEmpty(currentUser) && !currentUser.error) {
      isUserSignedIn.current = true

      if (redirectToRegisterAfterLogin) {
        redirectToRegister(false)
      }

      if (details && details.sourceType === 'opportunity' && details.id) {
        attemptRegistrationUpgradeFromOpportunity(details)
      }
    }
  }, [currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  if (apiError) {
    return <NotFound fallback={backFallback}>{apiErrorMessage}</NotFound>
  }

  if (!details.id || !allRoles || availableSlots === null) {
    return <Loader />
  }

  const shareMessage = `I just signed up to volunteer and help ${details.organization.name} with their event ${details.name}. Join me!`
  const allowMultipleRegistrations = isEffectiveAllowMultipleRegistrations(
    featureEnabledState,
    details.allowMultipleRegistrations,
  )
  const hasSingleRegistrationView = details.sourceType === 'registration' && !allowMultipleRegistrations
  const showSignedUpRegistrations =
    hasSingleRegistrationView || (allowMultipleRegistrations && myRegistrations.length > 0)
  const showHowYouCanHelp =
    details.status !== OpportunityStatus.Concluded &&
    (details.sourceType === 'opportunity' || (allowMultipleRegistrations && myRegistrations.length > 0))
  const hideDayOfInstructions = featureEnabledState
    ? !!getFeatureEnabled(featureEnabledState, 'HideDayOfInstructions')
    : false

  const redirectToRegister = (isEditMode: boolean) => {
    if (!currentUser || !details.id || !history) {
      return
    }

    if (match?.params.stableLinkCode) {
      const { organizationId, stableLinkCode } = match?.params

      const pathname = ROUTE_NAMES.stableLinkRegistration(organizationId, stableLinkCode)
      const state = {
        stableLinkCode,
        eventId: details.id,
        isEditMode,
      }

      history.push({ pathname, state })
    } else {
      const pathname = ROUTE_NAMES.eventRegistrationRefactor(
        organizationId,
        encodeOccurrence(details.opportunityId, details.startsAt, details.timeZone),
        token ? String(token) : undefined,
      )
      const eventRegistrationPathConfig = Object.assign({
        pathname,
        state: {
          eventId: encodeOccurrence(details.opportunityId, details.startsAt, details.timeZone),
          isEditMode,
        },
      })
      history.push(eventRegistrationPathConfig)
    }
  }

  const { locationIsAddress, locationAddressType, locationDetails, address, city, state, zip, mapPosition } = details

  const mapEmbedProps = getVolunteerEventMapEmbedProps({
    locationAddressType,
    locationIsAddress,
    locationDetails,
    address,
    city,
    state,
    zip,
    mapPosition,
  })

  const createMarkup = (html: string) => {
    if (html.includes('/script>')) {
      return { __html: '' }
    }
    return { __html: html }
  }

  return (
    <>
      <div className="event_details_wrapper">
        {!token && <EventDetailsBack fallback={backFallback} />}
        <div className="event_details_block">
          <div className="event_details_block_left">
            <div className="event_details_block_left_title">{details.name}</div>
            <div className="event_details_block_left_subtitle">
              <a href={details.organization.website}>{details.organization.name}</a>
            </div>
            <TimeAndPlace
              dateStart={details.startsAt}
              dateEnd={details.endsAt}
              {...{ address, city, state, zip, locationDetails, locationIsAddress, locationAddressType }}
              timeZone={details.timeZone}
            />
            <div className="event_details_block_left_description">
              {details.description && <div dangerouslySetInnerHTML={createMarkup(details.description)} />}
            </div>
            {details && details.instructions && !hideDayOfInstructions && (
              <div className="event_details_block_left_description">
                <div dangerouslySetInnerHTML={createMarkup(details.instructions)} />
              </div>
            )}
          </div>
          <div
            className={`event_details_block_right ${
              details.sourceType === 'opportunity' && 'event_details_block_right--mobile'
            }`}
          >
            <div className="event_details_block_right_map_wrapper">
              {mapEmbedProps ? (
                <div className="event_details_block_right_map">
                  <EmbeddedGoogleMap {...mapEmbedProps} />
                </div>
              ) : null}
              <FeatureEnabled code="SocialSharing">
                <div className="event_details_block_right_share">
                  <div className="event_details_block_right_share_social">
                    <IconComponent name="facebook" height="24px" width="24px" />
                    <FacebookShareButton url={pathName} quote={shareMessage}>
                      Share on Facebook
                    </FacebookShareButton>
                  </div>
                  <div className="event_details_block_right_share_social">
                    <IconComponent name="twitter" height="24px" width="24px" />
                    <TwitterShareButton url={pathName} title={shareMessage}>
                      Share on Twitter
                    </TwitterShareButton>
                  </div>
                </div>
              </FeatureEnabled>
              {(details.sourceType === 'opportunity' || allowMultipleRegistrations) &&
                (availableSlots > 0 ? (
                  <div
                    className={cn('event_details_block_right_register', {
                      disabled: details.missingRequiredMemberships || details.status === OpportunityStatus.Concluded,
                    })}
                    onClick={() => {
                      if (details.missingRequiredMemberships) {
                        return
                      }

                      return isUserSignedIn.current
                        ? redirectToRegister(false)
                        : setOpenLogin && setOpenLogin(true, true)
                    }}
                  >
                    Register
                  </div>
                ) : (
                  <div className="event_details_block_right_register_empty">This event is full</div>
                ))}
            </div>
            <div className="event_details_block_right_info_wrapper">
              {showSignedUpRegistrations && (
                <div className="event_details_block_right_info">
                  <div className="event_details_block_right_info_title">You’re signed up!</div>
                  {(hasSingleRegistrationView ? [{ id: details.id, details: details.details }] : myRegistrations).map(
                    (registration: { id: number; details: any[] }) => (
                      <div key={registration.id}>
                        {registration.details.map((item: any, index: number) => (
                          <div className="event_details_block_right_info_role" key={`${registration.id}-${index}`}>
                            <div className="event_details_block_right_info_role_icon" />
                            <div className="event_details_block_right_info_role_shift">
                              <div className="event_details_block_right_info_role_shift_time">
                                {`${item.timeshift.time_start} ${item.timeshift.time_end}`}
                              </div>
                              <div className="event_details_block_right_info_role_shift_slot">
                                Your role is <b>{item.role.name}</b>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div
                          className="event_details_block_right_cancel"
                          onClick={() => {
                            setCancelRegistrationId(registration.id)
                            setCancelPopup(true)
                          }}
                        >
                          Cancel Registration
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
              {showHowYouCanHelp && (
                <div className="event_details_block_right_info event_details_block_right_info--mobile">
                  <div className="event_details_block_right_info_title">How You Can Help</div>
                  <div className="event_details_block_right_info_subtitle">
                    {availableSlots > 0 ? `${availableSlots} Volunteers Still needed!` : 'No more Volunteers needed!'}
                  </div>
                  <div className="event_details_block_right_info_members">
                    {roleById.length > 0 &&
                      roleById.map((item: any, key: number) => {
                        return (
                          <WhoIsNeeded
                            key={key}
                            totalMembers={item.available}
                            timeShifts={details.timeshifts}
                            opportunity_timeshift_id={item.opportunity_timeshift_id}
                            roleName={item.name}
                            allRoles={allRoles}
                          />
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {
        <CancelRegistrationPopup
          eventName={details.name}
          eventId={cancelRegistrationId ?? details.id}
          isOpen={cancelPopup}
          onClosePopup={(value: boolean) => {
            setCancelPopup(value)
            setCancelRegistrationId(null)
          }}
          orgId={organizationId}
          {...props}
        />
      }
    </>
  )
}

const NotFound = ({ fallback, children }: NotFoundProps) => {
  return (
    <div className="event_details_wrapper">
      <EventDetailsBack fallback={fallback} />
      <div className="event_details_not_found">{children}</div>
    </div>
  )
}

export default withFeatureEnabled(VolunteersEventDetailsScene)
